import React, { useState } from 'react';
import SEO from './SEO';
import './App.css';

const Matrimony = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPages, setShowPages] = useState(false);

  const matrimonyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "OmkarMaratha Matrimonial Platform",
    "applicationCategory": "SocialNetworkingApplication",
    "operatingSystem": "Web Browser",
    "description": "A comprehensive matrimonial platform designed for the Marathi community, combining traditional values with modern technology.",
    "featureList": ["Kundali Matching", "Verified Profiles", "Privacy Controls", "In-app Messaging", "Gotra Verification"],
    "author": {
      "@type": "Organization",
      "name": "BRAMHAWARE"
    },
    "inLanguage": ["en", "mr"]
  };

  const slides = [
    {
      title: "OmkarMaratha Matrimonial Platform",
      subtitle: "मराठी वैवाहिक मंच | Connecting Marathi Hearts Worldwide",
      content: "A comprehensive matrimonial platform designed specifically for the Marathi community, combining traditional values with modern Spring Boot technology to help families find perfect matches."
    },
    {
      title: "Vision & Objectives | प्रकल्प दृष्टिकोन",
      content: [
        "Create the most trusted Marathi matrimonial platform",
        "Preserve cultural values with गोत्र, कुल, पंचांग-based matching",
        "Connect Marathi families across the globe",
        "Ensure 100% verified and authentic profiles",
        "Provide a safe and secure environment for matrimonial search"
      ]
    },
    {
      title: "Technology Stack",
      content: [
        "Frontend: Thymeleaf Templates, Bootstrap 5, JavaScript ES6+",
        "Backend: Spring Boot 3.x, Spring Security 6, Spring Data JPA",
        "Database: MySQL/PostgreSQL with Redis caching",
        "Authentication: JWT with multi-factor authentication",
        "Payment: Razorpay integration for secure processing",
        "Hosting: AWS Cloud with CDN for global reach",
        "Mobile: Progressive Web App (PWA) with responsive design"
      ]
    },
    {
      title: "Key Features",
      content: [
        "🔍 Advanced Search with cultural filters",
        "✅ 100% Manual Profile Verification",
        "🕉️ Kundali Matching & Astrological Compatibility",
        "📱 Mobile-First Responsive Design",
        "🔒 Privacy Controls & Secure Communication",
        "💬 In-app Messaging with family introductions",
        "📊 AI-powered Match Recommendations",
        "🏛️ गोत्र (Gotra) verification system"
      ]
    },
    {
      title: "User Journey",
      content: [
        "1. Free Registration with Email/Phone Verification",
        "2. Profile Creation with Photo & Document Verification",
        "3. Browse & Search Profiles with Cultural Filters",
        "4. Express Interest & Connect with Matches",
        "5. Cultural Background & गोत्र (Gotra) Verification",
        "6. Family Introductions & Communication",
        "7. Traditional Ceremony Planning Support"
      ]
    },
    {
      title: "System Architecture",
      content: [
        "🏗️ Spring Boot Microservices Architecture",
        "☁️ AWS Cloud-Native Design with Auto-Scaling",
        "🔄 Real-time Notifications with WebSocket",
        "📊 Analytics Dashboard with Spring Boot Admin",
        "🔐 Multi-layer Security with Spring Security",
        "🌐 Global CDN for Fast Loading",
        "📱 RESTful API Design for Mobile Integration"
      ]
    },
    {
      title: "Security & Privacy",
      content: [
        "🛡️ End-to-end Encryption for Messages",
        "🔒 GDPR Compliant Data Protection",
        "✅ Manual Profile Verification Process",
        "🚫 Anti-fraud Detection System",
        "👤 Privacy Controls for Profile Visibility",
        "📞 Secure Communication Channels",
        "🔍 Background Verification Services"
      ]
    },
    {
      title: "Development Timeline",
      content: [
        "📅 Phase 1 (Months 1-3): Core Spring Boot Platform Development",
        "🔧 Phase 2 (Months 4-5): Advanced Features & Testing",
        "🚀 Phase 3 (Month 6): Launch & User Onboarding",
        "📈 Phase 4 (Months 7-9): Growth & Optimization",
        "🌟 Phase 5 (Months 10-12): Advanced AI Features",
        "🔄 Ongoing: Continuous Improvement & Support"
      ]
    },
    {
      title: "Community Impact & Opportunity",
      content: [
        "🎯 Target Community: 80+ Million Marathi Speakers Globally",
        "🌍 Global Reach: Marathi Diaspora Worldwide",
        "🤝 Partnership Opportunities with Cultural Organizations",
        "📈 Growing Online Adoption in Matrimonial Services",
        "🏛️ Cultural Preservation through Technology",
        "💝 Strong Community Bonds and Trust"
      ]
    },
    {
      title: "Success Metrics & Community KPIs",
      content: [
        "👥 Community Base: 10,000+ Verified Families in Year 1",
        "💑 Success Rate: High-quality Matches with Cultural Compatibility",
        "⭐ User Satisfaction: 95%+ Community Approval Rating",
        "🔄 Retention Rate: 70%+ Monthly Active Users",
        "🌐 Global Reach: International Marathi Community Coverage",
        "🏆 Cultural Impact: Preserving Traditional Values"
      ]
    },
    {
      title: "Future Enhancements",
      content: [
        "🤖 AI-Powered Cultural Compatibility Matching",
        "🎥 Video Profiles & Virtual Family Meetings",
        "🎪 Virtual Matrimonial Events & Cultural Programs",
        "📱 Native Mobile Apps for iOS & Android",
        "🌍 International Community Expansion",
        "🔗 Integration with Cultural Platforms",
        "📊 Advanced Analytics for Better Cultural Matching"
      ]
    },
    {
      title: "Next Steps",
      content: [
        "✅ Finalize Spring Boot Architecture & Development Team",
        "💰 Secure Project Funding & Resources",
        "🏗️ Begin Development with Cultural-First MVP Features",
        "🤝 Partner with Marathi Cultural Organizations",
        "📈 Launch Beta Version with Community Leaders",
        "🚀 Full Launch with Cultural Marketing Campaign",
        "📊 Monitor, Analyze & Continuously Improve"
      ]
    }
  ];

  const pages = [
    { name: "Home", description: "Main landing page with search functionality", file: "index.html" },
    { name: "Search", description: "Advanced profile search with filters", file: "search.html" },
    { name: "Login", description: "User authentication page", file: "login.html" },
    { name: "Register", description: "New user registration form", file: "register.html" },
    { name: "Profile Details", description: "Detailed profile view", file: "profile-detail.html" },
    { name: "Subscription Plans", description: "Pricing and subscription options", file: "plans.html" }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const openPage = (filename) => {
    // Create a new window to display the matrimonial page
    const matrimonyPath = `/Matrimony/${filename}`;
    window.open(matrimonyPath, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };

  if (showPages) {
    return (
      <div className="matrimony-container">
        <div className="matrimony-header">
          <button 
            className="back-btn"
            onClick={() => setShowPages(false)}
          >
            ← Back to Presentation
          </button>
          <h1 className="matrimony-title">OmkarMaratha Frontend Pages</h1>
        </div>

        <div className="pages-grid">
          {pages.map((page, index) => (
            <div key={index} className="page-card">
              <div className="page-icon">
                <i className="fas fa-file-alt"></i>
              </div>
              <h3>{page.name}</h3>
              <p>{page.description}</p>
              <button 
                className="view-page-btn"
                onClick={() => openPage(page.file)}
              >
                View Page
              </button>
            </div>
          ))}
        </div>

        <div className="pages-footer">
          <p>Click on any page card to view the actual frontend implementation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="matrimony-container">
      <SEO
        title="OmkarMaratha Matrimonial Platform | Marathi Matrimony | BRAMHAWARE"
        description="Find your perfect life partner with OmkarMaratha - A trusted Marathi matrimonial platform with Kundali matching, verified profiles, Gotra verification, and traditional values. Connect with Marathi families worldwide."
        keywords="Marathi Matrimony, Marathi Marriage, Kundali Matching, Marathi Vivah, OmkarMaratha, Gotra Verification, Marathi Wedding, BRAMHAWARE Matrimony, Pune Matrimony"
        canonicalUrl="/matrimony"
        structuredData={matrimonyStructuredData}
      />
      <div className="matrimony-header">
        <h1 className="matrimony-title">OmkarMaratha Matrimonial Platform</h1>
        <div className="matrimony-nav-buttons">
          <button 
            className="pages-btn"
            onClick={() => setShowPages(true)}
          >
            View Sample Pages
          </button>
        </div>
      </div>

      <div className="presentation-container">
        <div className="slide-counter">
          Slide {currentSlide + 1} of {slides.length}
        </div>

        <div className="slide">
          <h2 className="slide-title">{slides[currentSlide].title}</h2>
          {slides[currentSlide].subtitle && (
            <h3 className="slide-subtitle">{slides[currentSlide].subtitle}</h3>
          )}
          
          <div className="slide-content">
            {Array.isArray(slides[currentSlide].content) ? (
              <ul className="slide-list">
                {slides[currentSlide].content.map((item, index) => (
                  <li key={index} className="slide-list-item">{item}</li>
                ))}
              </ul>
            ) : (
              <p className="slide-text">{slides[currentSlide].content}</p>
            )}
          </div>
        </div>

        <div className="slide-navigation">
          <button 
            className="nav-btn prev-btn" 
            onClick={prevSlide}
            disabled={currentSlide === 0}
          >
            ← Previous
          </button>
          
          <div className="slide-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
          
          <button 
            className="nav-btn next-btn" 
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
          >
            Next →
          </button>
        </div>

        <div className="slide-progress">
          <div 
            className="progress-bar" 
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="matrimony-footer">
        <p>Use arrow keys or navigation buttons to browse through the presentation</p>
      </div>
    </div>
  );
};

export default Matrimony;
